import { useTranslation } from 'react-i18next';
import { PropsWithChildren } from 'react';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import { rgba } from 'polished';

import { Link } from '@treams/treams-routes';
import { Callout, Card, Heading, Paragraph, Subheading } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import ConfigurationField from './fields/ConfigurationField';

const FieldCard = styled(Box)<{ error?: boolean }>`
	border: 1px solid
		${(props) => (props.error ? props.theme.colors.red : props.theme.colors.lightBlue)};
	box-shadow: 0px 0px 0px 2px
		${(props) => rgba(props.error ? props.theme.colors.red : props.theme.colors.lightBlue, 0.25)};
	border-radius: 5px;
`;

export const SharedFields: { [key: string]: { title: string; description: string } } = {
	goLiveDate: {
		title: 'Go-live datum',
		description: 'Selecteer de datum dat de medewerkers toegang krijgen tot Treams.',
	},

	syncAvatars: {
		title: 'Avatars synchroniseren',
		description:
			'Wil je de avatar (profielfoto) van de medewerker synchroniseren via de koppeling? Medewerkers kunnen hun avatar dan niet meer handmatig aanpassen.',
	},

	syncLocale: {
		title: 'Taal synchroniseren',
		description:
			'Wil je de taal van de medewerker synchroniseren via de koppeling? Medewerkers kunnen hun taal dan niet meer handmatig aanpassen.',
	},

	defaultLocale: {
		title: 'Standaard taal',
		description:
			'Selecteer een taal die wordt gebruikt bij nieuwe medewerkers wanneer er geen is ingesteld bij de medewerker.',
	},

	syncJobs: {
		title: 'Functies synchroniseren',
		description: 'Wil je de functies van medewerker synchroniseren via de koppeling?',
	},

	syncManagers: {
		title: 'Managementrechten synchroniseren',
		description: 'Wil je managementrechten synchroniseren via de koppeling?',
	},

	employeeFilter: {
		title: 'Kies welke medewerkers in Treams verschijnen',
		description:
			'Wil je geen medewerkers synchroniseren? Als je dit inschakelt, wordt alleen de organisatiestructuur gesynchroniseerd.',
	},

	dryRun: {
		title: 'Test-modus',
		description:
			'Zet dit aan om te bekijken welke wijzigingen in het systeem woorden doorgevoerd zonder de wijzigingen daadwerkelijk uit te voeren.',
	},

	'twoWay.locale': {
		title: 'Taal tweeweg-koppeling',
		description: 'Selecteer in welke taal de bestanden voor de tweeweg-koppeling worden geüpload.',
	},
};

const FieldStatus = styled(Box)<{ error?: boolean }>`
	flex-grow: 0;
	flex-shrink: 0;
	border-radius: 4px;

	background: ${(props) =>
		rgba(props.error ? props.theme.colors.red : props.theme.colors.green, 0.25)};
	color: ${(props) => (props.error ? props.theme.colors.red : props.theme.colors.greenDark)};
`;

export function FieldContainer(
	props: PropsWithChildren<{
		field: graphql.ConnectorConfigField;
		translate: (key: string) => { title: string; description: string } | undefined;
	}>,
) {
	const { t } = useTranslation();

	return (
		<FieldCard key={props.field.key} mb={3} p={3} error={!props.field.isConfigured}>
			<Flex alignItems="flex-start">
				<Box flexGrow={1} flexShrink={1}>
					<Subheading>{props.translate(props.field.key)?.title}</Subheading>
					<Paragraph>{props.translate(props.field.key)?.description}</Paragraph>
				</Box>
				<FieldStatus error={!props.field.isConfigured} px={3} py={2} ml={2}>
					{props.field.isConfigured
						? t('connectors.settings.configuration.configured')
						: t('connectors.settings.configuration.required')}
				</FieldStatus>
			</Flex>
			<Box mt={2}>{props.children}</Box>
		</FieldCard>
	);
}

FieldContainer.Skeleton = function FieldContainerSkeleton() {
	return (
		<Card.Skeleton>
			<Flex alignItems="flex-start">
				<Box flexGrow={1} flexShrink={1}>
					<Subheading.Skeleton mb={2} />
					<Paragraph.Skeleton lines={2} />
				</Box>
			</Flex>
		</Card.Skeleton>
	);
};

export default function ProductConfiguration(props: {
	connector: graphql.Connector;
	translate: (key: string) => { title: string; description: string } | undefined;
	documentationLink?: string;
}) {
	const { t } = useTranslation();

	const fields = props.connector.config;

	const [update] = graphql.useUpdateProductConfigurationMutation();

	return (
		<Box>
			{props.documentationLink && (
				<Callout color="blue">
					<Link to={props.documentationLink} target="_blank">
						{t('connectors.settings.configuration.callout')}
					</Link>
				</Callout>
			)}
			<Heading my={3}>{t('connectors.settings.configuration.title')}</Heading>
			{fields.map((field) => (
				<FieldContainer key={field.id} field={field} translate={props.translate}>
					<ConfigurationField
						connectorType={props.connector.type}
						field={field}
						onSave={async (input) => {
							await update({
								variables: {
									input: {
										...input,
										key: field.key,
										connectorType: props.connector.type,
									},
								},
							});
						}}
					/>
				</FieldContainer>
			))}
		</Box>
	);
}
